import { Banknote, ChevronDown } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

import { codeCurrencyOptions } from './constants/statusOptions'

export const AmountFilter = ({
  amountFrom,
  amountTo,
  selectedCodeCurrency,
  onAmountRangeChange,
  onCodeCurrencyChange,
}: {
  amountFrom: string | null | undefined
  amountTo: string | null | undefined
  selectedCodeCurrency: string
  onAmountRangeChange: (
    _amountFrom: string | null,
    _amountTo: string | null
  ) => void
  onCodeCurrencyChange: (_codeCurrency: string) => void
}) => {
  // Local state for input values to allow editing before applying
  const [localAmountFrom, setLocalAmountFrom] = useState(amountFrom || '')
  const [localAmountTo, setLocalAmountTo] = useState(amountTo || '')
  const [localCodeCurrency, setLocalCodeCurrency] =
    useState(selectedCodeCurrency)

  // Sync local state when props change (e.g., when filters are cleared externally)
  useEffect(() => {
    setLocalAmountFrom(amountFrom || '')
    setLocalAmountTo(amountTo || '')
    setLocalCodeCurrency(selectedCodeCurrency)
  }, [amountFrom, amountTo, selectedCodeCurrency])

  // Helper function to format and validate number input
  const formatNumberInput = (value: string): string => {
    // Remove non-numeric characters except decimal point
    const cleaned = value.replace(/[^0-9.]/g, '')
    // Ensure only one decimal point
    const parts = cleaned.split('.')
    if (parts.length > 2) {
      return parts[0] + '.' + parts.slice(1).join('')
    }
    return cleaned
  }

  const handleAmountFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatNumberInput(e.target.value)
    setLocalAmountFrom(formatted)
  }

  const handleAmountToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatNumberInput(e.target.value)
    setLocalAmountTo(formatted)
  }

  const handleApply = () => {
    let fromValue =
      localAmountFrom.trim() === '' ? null : Number(localAmountFrom)
    let toValue = localAmountTo.trim() === '' ? null : Number(localAmountTo)

    if (fromValue !== null && toValue !== null && fromValue > toValue) {
      const temp = fromValue
      fromValue = toValue
      toValue = temp
    }

    const fromString = fromValue !== null ? String(fromValue) : null
    const toString = toValue !== null ? String(toValue) : null

    onAmountRangeChange(fromString, toString)
    onCodeCurrencyChange?.(localCodeCurrency)
  }

  const handleClear = () => {
    setLocalAmountFrom('')
    setLocalAmountTo('')
    onAmountRangeChange(null, null)
    // Reset currency to default when clearing amounts
    onCodeCurrencyChange('CUP')
  }

  // Check if there are active filters
  const hasActiveAmountFilter = amountFrom || amountTo
  const hasLocalChanges =
    localAmountFrom !== (amountFrom || '') || localAmountTo !== (amountTo || '')
  const hasAnyAmountInput = localAmountFrom || localAmountTo

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          aria-label="Rango de importe"
          className="gap-1.5"
          variant="outline"
        >
          <Banknote className="h-4 w-4" />
          {hasActiveAmountFilter ? (
            <span className="font-medium">
              {amountFrom && amountTo
                ? `${amountFrom} - ${amountTo} ${selectedCodeCurrency}`
                : amountFrom
                  ? `≥ ${amountFrom} ${selectedCodeCurrency}`
                  : `≤ ${amountTo} ${selectedCodeCurrency}`}
            </span>
          ) : (
            <span>Importe</span>
          )}
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[280px]">
        <div className="grid gap-4">
          <div className="space-y-2">
            <h4 className="font-medium">Rango de importe</h4>
            <div className="grid grid-cols-2 gap-2">
              <div className="grid gap-1.5">
                <Label htmlFor="amount-min">Mínimo</Label>
                <Input
                  id="amount-min"
                  inputMode="decimal"
                  onChange={handleAmountFromChange}
                  placeholder="0.00"
                  type="text"
                  value={localAmountFrom}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="amount-max">Máximo</Label>
                <Input
                  id="amount-max"
                  inputMode="decimal"
                  onChange={handleAmountToChange}
                  placeholder="1000.00"
                  type="text"
                  value={localAmountTo}
                />
              </div>
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="currency">Moneda</Label>
            <Select
              disabled={
                !localAmountFrom && !localAmountTo && !amountFrom && !amountTo
              }
              onValueChange={setLocalCodeCurrency}
              value={localCodeCurrency}
            >
              <SelectTrigger id="currency">
                <SelectValue placeholder="Seleccionar moneda" />
              </SelectTrigger>
              <SelectContent>
                {codeCurrencyOptions.map((option) => (
                  <SelectItem
                    className="cursor-pointer"
                    key={option.value}
                    value={option.value}
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-between">
            <Button
              disabled={!hasActiveAmountFilter && !hasAnyAmountInput}
              onClick={handleClear}
              size="sm"
              variant="ghost"
            >
              Limpiar
            </Button>
            <Button
              disabled={!hasLocalChanges && !hasAnyAmountInput}
              onClick={handleApply}
              size="sm"
            >
              Aplicar
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
